/**
 * Helper functions for the passage places map component
 */

/**
 * Parse date strings to years for timeline calculations
 * Handles various date formats including BC/AD and circa dates
 */
export function parseDateToYear(dateStr: string, isStart: boolean): number {
    // Handle cross-era ranges like "146 BC - 330 AD", "63 BC - AD 324", or "31 BC - c. 330 AD"
    // Updated to handle both "BC - 330 AD" and "BC - AD 324" formats
    const crossEraMatch = dateStr.match(/(\d+)\s*(BC|AD)\s*-\s*(?:(AD|BC)\s+)?(?:c\.\s*)?(\d+)\s*(BC|AD)?/i);
    if (crossEraMatch) {
        const year1 = parseInt(crossEraMatch[1]);
        const era1 = crossEraMatch[2].toUpperCase();
        const year2 = parseInt(crossEraMatch[4]);
        // Era2 can be in position 3 (before number) or position 5 (after number)
        const era2 = (crossEraMatch[3] || crossEraMatch[5])?.toUpperCase();

        // Only process as cross-era if we have both eras
        if (era2) {
            const startYear = era1 === 'BC' ? -year1 : year1;
            const endYear = era2 === 'BC' ? -year2 : year2;

            return isStart ? startYear : endYear;
        }
    }

    // Handle same-era ranges like "1000-586 BC" or "c. 47-49 AD"
    const match = dateStr.match(/(?:c\.\s*)?(\d+)(?:-(\d+))?\s*(BC|AD)?/i);
    if (!match) return isStart ? -1000 : 100; // Default fallback

    const year1 = parseInt(match[1]);
    const year2 = match[2] ? parseInt(match[2]) : year1;
    const era = match[3]?.toUpperCase();

    const multiplier = era === 'BC' ? -1 : 1;
    const result = isStart ? year1 * multiplier : year2 * multiplier;

    return result;
}

/**
 * Combine passage and book events into a single array
 */
export function combinePassageAndBookEvents(
    historicalEventsByPassage: HistoricalEventsByPassage | null,
    historicalEventsByBook: HistoricalEventsByBook | null
): HistoricalEventByPassage[] {
    const passageEvents = historicalEventsByPassage?.events || [];
    const bookEvents = historicalEventsByBook?.events || [];
    return [...passageEvents, ...bookEvents];
}

/**
 * Collect all event titles from passage/book events
 */
export function getRelevantEventTitles(
    historicalEventsByPassage: HistoricalEventsByPassage | null,
    historicalEventsByBook: HistoricalEventsByBook | null
): Set<string> {
    const eventTitles = new Set<string>();
    const allEvents = combinePassageAndBookEvents(historicalEventsByPassage, historicalEventsByBook);

    allEvents.forEach(event => {
        eventTitles.add(event.title);
    });

    return eventTitles;
}

/**
 * Compute time range from historical periods
 */
export function computeTimeRange(historicalPeriods?: Array<{ dates: string }>): { earliestYear: number; latestYear: number } | null {
    if (!historicalPeriods || historicalPeriods.length === 0) return null;

    const years = historicalPeriods.flatMap(period => {
        const start = parseDateToYear(period.dates, true);
        const end = parseDateToYear(period.dates, false);
        return [start, end];
    });

    return {
        earliestYear: Math.min(...years),
        latestYear: Math.max(...years)
    };
}

/**
 * Format a year value for display (BC/AD)
 */
export function formatYear(year: number): string {
    if (year < 0) {
        return `${Math.abs(year)} BC`;
    }
    return `${year} AD`;
}

